import { StateCreator } from 'zustand';
import { IDEState } from './useIDEStore';
import { LanguageService } from '../languages/languageService';

export const withCompilerInitialization =
    (f: StateCreator<IDEState>) =>
        (set, get, api) => {
            const store = f(set, get, api);

            // Wait for next tick to ensure store is initialized before accessing state
            setTimeout(async () => {
                try {
                    const state = get();
                    if (state && !state.compilerReady && state.compilerService) {
                        await state.compilerService.init();
                        set({ compilerReady: true });
                    }
                } catch (error) {
                    console.error('Compiler initialization failed:', error);
                }
            }, 0);

            return store;
        };

// Then, update the withLanguageServiceInitialization middleware with the reactive approach:
export const withLanguageServiceInitialization =
    (f: StateCreator<IDEState>) =>
        (set, get, api) => {
            // Setup subscription for language service initialization
            const unsub = api.subscribe(
                (state) => ({
                    monaco: state.monaco,
                    editorRef: state.editorRef,
                    compilerService: state.compilerService,
                    compilerReady: state.compilerReady,
                    project: state.project,
                    files: state.files,
                    languageService: state.languageService
                }),
                (current, previous) => {
                    if (
                        current.monaco &&
                        current.editorRef?.current &&
                        current.compilerService &&
                        current.compilerReady &&
                        current.project &&
                        current.files.length > 0 &&
                        !current.languageService
                    ) {
                        console.log('[Middleware] Initializing language service');
                        const languageService = new LanguageService(
                            current.monaco,
                            current.editorRef.current,
                            current.compilerService,
                            current.project,
                            current.files
                        );
                        set({ languageService });
                    }
                }
            );

            // Create the actual store
            const store = f(set, get, api);

            // Return a function to unsubscribe when the store is destroyed
            api.onDestroy = () => {
                unsub();
            };

            return store;
        };